import { ROUTE_PATH } from '@/constants/route-path';
import { WalletContext } from '@/contexts/wallet-context';
import { IUserGameInfo } from '@/interfaces/api/gamefi';
import {
  IGetPlayerPoolProfile,
  TokenDetail,
} from '@/interfaces/api/player-share';
import { getGroupMessageRoomId } from '@/services/chat/group';
import CPassTokenAPI from '@/services/classes/passToken';
import { getUserGameInfo } from '@/services/gamefi';
import {
  getListMentionByKeyWord,
  getPlayerPoolProfile,
  getPlayerTokenDetail,
  getTokenHolders,
  getTokenHolding,
} from '@/services/player-share';
import { compareString } from '@/utils';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isAddress } from 'web3-utils';
import { useDM } from '../DirectMessage/provider';
import profilesContext from './context';

type Props = Pick<React.ComponentPropsWithoutRef<'div'>, 'children'>;
type IHolderListItem = {
  name: string;
  avatar: string;
};

export default function ProfilesProvider({ children }: Props) {
  const router = useRouter();
  const {
    query: { id },
  } = router;
  const passTokenAPI = useRef(new CPassTokenAPI()).current;

  const { addressL2 } = useContext(WalletContext);
  const { tryGetRoomByIdAndAddToYourRoom, rooms } = useDM();
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(true);
  const [playerPoolProfile, setPlayerPoolProfile] =
    useState<IGetPlayerPoolProfile | null>(null);
  const [yourPoolProfile, setYourPoolProfile] =
    useState<IGetPlayerPoolProfile | null>(null);
  const [userGameInfo, setUserGameInfo] = useState<IUserGameInfo | undefined>();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [playerTokenDetail, setPlayerTokenDetail] =
    useState<TokenDetail | null>(null);

  const [isTokenHolding, setIsTokenHolding] = useState<boolean>(false);
  const [isTokenHolded, setIsTokenHolded] = useState<boolean>(false);

  const [balanceTokenHolded, setBalanceTokenHolded] = useState<number>(0);
  const [balanceTokenHolding, setBalanceTokenHolding] = useState<number>(0);

  const [balancePassTokenHolded, setBalancePassTokenHolded] =
    useState<number>(0);
  const [balancePassTokenHolding, setBalancePassTokenHolding] =
    useState<number>(0);

  const [isTokenHoldingLoading, setIsTokenHoldingLoading] =
    useState<boolean>(true);
  const [keyBalance, setKeyBalance] = useState(0);
  const [keyRemaining, setKeyRemaining] = useState(0);
  const [holderLists, setHolderList] = useState<any[]>([]);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [tabActive, setTabActive] = useState<number>(0);
  const [userChatRoom, setUserChatRoom] = useState({
    dm: '',
    circle: '',
    community: '',
  });

  const isProfileAddress = useMemo(() => {
    return isAddress(id as string);
  }, [id]);

  const handleGetUserGameInfo = async (
    address: string,
    callback?: (data: IUserGameInfo) => void
  ) => {
    try {
      const data = await getUserGameInfo(address);
      setUserGameInfo(data);
      callback && callback(data);
    } catch (error) {}
  };

  const hasDmRoom = useMemo(() => {
    const dmRooms = rooms.filter(room => room.type === 'CHAT_ROOM');
    const hasFoundRoom = dmRooms.find(room => {
      return compareString(room.owner, playerPoolProfile?.address);
    });
    return hasFoundRoom;
  }, [rooms, playerPoolProfile?.address]);

  useEffect(() => {
    if (playerPoolProfile?.tokenAddress) {
      getGroupMessageRoomId({
        token: playerPoolProfile?.tokenAddress,
      }).then(room => {
        if (room) {
          setUserChatRoom(prev => ({
            ...prev,
            circle: room?.roomId,
          }));
          tryGetRoomByIdAndAddToYourRoom(room?.roomId);
        }
      });
    }
  }, [playerPoolProfile?.tokenAddress]);

  useEffect(() => {
    if (playerPoolProfile?.passTokenAddress) {
      getGroupMessageRoomId({
        token: playerPoolProfile?.passTokenAddress,
      }).then(room => {
        if (room) {
          setUserChatRoom(prev => ({
            ...prev,
            community: room?.roomId,
          }));
          tryGetRoomByIdAndAddToYourRoom(room?.roomId);
        }
      });
    }
  }, [playerPoolProfile?.passTokenAddress]);

  useEffect(() => {
    if (!!hasDmRoom && hasDmRoom?.id) {
      setUserChatRoom(prev => ({
        ...prev,
        dm: hasDmRoom?.id,
      }));
    }
  }, [hasDmRoom]);

  useEffect(() => {
    const fetchYourProfile = async () => {
      // get your profile
      const res = await getPlayerPoolProfile(
        addressL2 as string,
        addressL2 as string
      );
      setYourPoolProfile(res);
    };
    fetchYourProfile();
  }, [addressL2]);

  const fetchProfile = async (): Promise<void> => {
    if (id) {
      try {
        handleGetUserGameInfo(id as string);
        // get view profile

        if (isProfileAddress) {
          const res = await getPlayerPoolProfile(id as string, addressL2);
          setPlayerPoolProfile(res);
        } else {
          const res = await getListMentionByKeyWord(
            addressL2 as string,
            id as string
          );
          if (!res) return;
          const { address } = res[0];
          const res2 = await getPlayerPoolProfile(address, addressL2);
          setPlayerPoolProfile(res2);
        }
      } catch (error) {
        console.log('getPlayerPoolProfileByAddress Error:', error);
        router.push(ROUTE_PATH.ALPHA_MOBILE_HOME);
      } finally {
        setIsProfileLoading(false);
      }
    } else {
      setIsProfileLoading(false);
    }
  };

  const handleCheckTokenHolding = async (_id: string) => {
    try {
      const res = await getTokenHolding({
        address: addressL2 as string,
        player_address: _id as string,
      });

      if (res.length === 1) {
        setBalanceTokenHolding(Number(res[0].balance));
      }

      const resPass = await passTokenAPI.getBalancePassTokenHolding({
        address: addressL2 as string,
        player_address: _id as string,
      });

      if (resPass.length === 1) {
        setBalancePassTokenHolding(Number(resPass[0].balance));
      }

      if (`${addressL2}`.toLowerCase() === `${_id}`.toLowerCase()) {
        setIsTokenHolding(true);
        setIsTokenHoldingLoading(false);
        setKeyBalance(1);
      } else {
        if (res.length === 1) {
          setKeyBalance(Number(res[0].balance));
          const ftBalance = Number(res[0].ftBalance || '0');

          if (ftBalance && ftBalance > 0) {
            setIsTokenHolding(true);
            return;
          }
          if (
            Number(res[0]?.balance) >=
            Number(playerPoolProfile?.minHoldingRequirement)
          ) {
            setIsTokenHolding(true);
          } else {
            setKeyRemaining(
              Number(playerPoolProfile?.minHoldingRequirement) -
                Number(res[0]?.balance)
            );
          }
        }
      }
    } finally {
      setIsTokenHoldingLoading(false);
    }
  };

  const handleCheckTokenHolded = async (_id: string) => {
    if (compareString(_id, addressL2)) {
      return;
    }

    try {
      const res = await getTokenHolding({
        address: _id as string,
        player_address: addressL2 as string,
      });

      if (res.length === 1 && Number(res[0]?.balance) > 0) {
        setBalanceTokenHolded(res.length ? Number(res[0]?.balance) || 0 : 0);
        setIsTokenHolded(true);
      }

      const resPass = await passTokenAPI.getBalancePassTokenHolding({
        address: _id as string,
        player_address: addressL2 as string,
      });

      if (resPass.length === 1 && Number(resPass[0]?.balance) > 0) {
        setBalancePassTokenHolded(
          resPass.length ? Number(resPass[0]?.balance) || 0 : 0
        );
      }
    } finally {
    }
  };

  const getListHoldersReview = async (_id: string) => {
    try {
      const res = await getTokenHolders({
        address: _id as string,
        page: 1,
        limit: 2,
      });
      setHolderList(res);
    } finally {
    }
  };

  useEffect(() => {
    const verifyYourTokenHolding = async () => {
      setIsTokenHoldingLoading(true);
      if (addressL2 && id) {
        if (isProfileAddress) {
          handleCheckTokenHolding(id as string);
          handleCheckTokenHolded(id as string);
          getListHoldersReview(id as string);
        } else {
          const res = await getListMentionByKeyWord(
            addressL2 as string,
            id as string
          );
          if (!res || !res.length) return;
          const { address } = res[0];
          handleCheckTokenHolding(address);
          handleCheckTokenHolded(address);
          getListHoldersReview(address);
        }
      }
    };
    if (playerPoolProfile) {
      verifyYourTokenHolding();
    }
  }, [id, addressL2, playerPoolProfile]);

  const fetchToken = async () => {
    try {
      const res: any = await getPlayerTokenDetail({
        address: id as string,
      });
      setPlayerTokenDetail(res);
    } catch (error) {
      console.log('getPlayerToken error: ', error);
    } finally {
      //
    }
  };

  const fetchData = () => {
    fetchProfile();
    fetchToken();
  };

  useEffect(() => {
    fetchData();

    // const intervalId = setInterval(fetchData, 10000);
    // return () => clearInterval(intervalId);
  }, [id]);

  const values = useMemo(() => {
    return {
      isProfileLoading,
      setIsProfileLoading,
      playerPoolProfile,
      setPlayerPoolProfile,
      showCreatePost,
      setShowCreatePost,
      userGameInfo,
      setUserGameInfo,
      yourPoolProfile,
      playerTokenDetail,
      isTokenHolding,
      isTokenHolded,
      isTokenHoldingLoading,
      keyBalance,
      keyRemaining,
      balanceTokenHolded,
      balanceTokenHolding,
      balancePassTokenHolded,
      balancePassTokenHolding,
      holderLists,
      fetchData,
      isSticky,
      setIsSticky,
      tabActive,
      setTabActive,
      userChatRoom,
    };
  }, [
    isProfileLoading,
    setIsProfileLoading,
    playerPoolProfile,
    setPlayerPoolProfile,
    showCreatePost,
    setShowCreatePost,
    userGameInfo,
    setUserGameInfo,
    yourPoolProfile,
    playerTokenDetail,
    isTokenHolding,
    isTokenHolded,
    isTokenHoldingLoading,
    keyBalance,
    keyRemaining,
    balanceTokenHolded,
    balanceTokenHolding,
    balancePassTokenHolded,
    balancePassTokenHolding,
    holderLists,
    fetchData,
    isSticky,
    setIsSticky,
    tabActive,
    setTabActive,
    userChatRoom,
  ]);

  return (
    <profilesContext.Provider value={values}>
      {children}
    </profilesContext.Provider>
  );
}

export function useProfiles() {
  return useContext(profilesContext);
}
